import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDyteMeeting } from '@dytesdk/react-web-core';
import { useMutation, useQueryClient } from 'react-query';
import { DyteParticipant } from '@dytesdk/web-core';
import InterviewNavbar from './InterviewNavbar';
import { useUserContext } from 'src/context/UserProvider';
import { UserRole } from 'types';
import { isUserAdmin } from 'shared-utils';
import PostJoinedScreen from './PostJoinedScreen';
import WaitingForHost from './WaitingForHost';
import JoinConfirmation from './JoinConfirmation';
import InterviewFooter from './InterviewFooter';
import { endMeeting, updateEndMeetingFlags } from 'src/apis/meeting';
import { useGetOngoingAssessmentDetails } from 'src/hooks/meeting.hooks';
import { useMeetingContext } from 'src/context/MeetingProvider';
import { get } from 'lodash';
import { useToast } from 'src/context/ToastProvider';
import MeetingEndModal from '../shared/MeetingEndModal';
import LiveInterviewOverlayModal from './LiveInterviewOverlayModal';

type Props = {
	isHost: boolean;
};

const LiveInterviewScreen = ({ isHost }: Props) => {
	const toast = useToast();
	const [showEndMeetingModal, setShowEndMeetingModal] = useState(false);
	const { user: session, isMentorLoggedIn, isJobseekerLoggedIn } = useUserContext();
	const dyteMeeting = useDyteMeeting();
	const navigate = useNavigate();
	const { meeting } = useMeetingContext();
	const queryClient = useQueryClient();
	const [joined, setJoined] = useState(false);
	const [hostJoined, setHostJoined] = useState(isHost);
	const [screenShareParticipant, setScreenShareParticipant] = useState<DyteParticipant>();
	const [participant, setParticipant] = useState<DyteParticipant>();

	const [isRescheduleRecommended, setIsRescheduleRecommended] = useState<boolean>(true);
	const [rescheduleReason, setRescheduleReason] = useState<string>('');

	const {
		setOngoingAssessDetails,
		setHasAssessmentEnded,
		setRefetchUserDetails,
		setPredictedUserSalary,
		setRecommendedUserPath,
	} = useMeetingContext();

	const { data: ongoingAssessData } = useGetOngoingAssessmentDetails(meeting?._id, data => {
		setHasAssessmentEnded(data.hasAssessmentEnded ?? false);
		setPredictedUserSalary(data.predictedUserSalary ?? undefined);
		setRecommendedUserPath(data.recommendedUserPath ?? undefined);
	});

	const endMeetingFn = useMutation({
		mutationKey: 'endMeeting',
		mutationFn: endMeeting,
		onSuccess(data) {
			updateMeetingFn.mutate({
				meetingId: meeting?._id ?? '',
				recommendReschedule: !isRescheduleRecommended,
				reasonForReschedule: rescheduleReason,
			});
			queryClient.refetchQueries(['getRounds']);
			queryClient.refetchQueries(['getMeetings']);
			queryClient.refetchQueries(['verifyCookie']);
			navigate(`/mentors/interviews`);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			queryClient.refetchQueries(['getRounds']);
			queryClient.refetchQueries(['getMeetings']);
			queryClient.refetchQueries(['verifyCookie']);
		},
	});

	const updateMeetingFn = useMutation({
		mutationKey: 'updateEndMeetingFlags',
		mutationFn: updateEndMeetingFlags,
		onSuccess(data) {
			dyteMeeting.meeting.participants.kickAll();
			queryClient.refetchQueries(['getRounds']);
			queryClient.refetchQueries(['getMeetings']);
			queryClient.refetchQueries(['verifyCookie']);
			navigate(`/mentors/interviews`);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
			queryClient.refetchQueries(['getRounds']);
			queryClient.refetchQueries(['getMeetings']);
			queryClient.refetchQueries(['verifyCookie']);
		},
	});

	useEffect(() => {
		window.addEventListener('message', e => {
			const data = e.data;
			if (data.type === 'assessment-ended') {
				queryClient.refetchQueries(['getOngoingAssessmentDetails']);
				// setHasAssessmentEnded(data.hasEnded);
			}
		});
		let participants = dyteMeeting.meeting.participants.joined.toArray();
		if (participants.length > 0) {
			setParticipant(participants[0]);
			setHostJoined(isHost || true);
		}
		dyteMeeting.meeting.self.on('roomJoined', () => {
			setJoined(true);
		});
		dyteMeeting.meeting.self.addListener('roomLeft', ({ state }) => {
			queryClient.refetchQueries(['getRounds']);
			queryClient.refetchQueries(['getMeetings']);
			queryClient.refetchQueries(['verifyCookie']);
			setRefetchUserDetails(true);
			if (state === 'ended') {
				setShowEndMeetingModal(true);
				if (isMentorLoggedIn) {
					// endMeetingFn.mutate(meeting?._id ?? '');
				} else if (isJobseekerLoggedIn) {
					// navigate('/dashboard');
					navigate('/my-applications');
				}
			} else if (state === 'left') {
				if (isUserAdmin(session?.role as UserRole)) navigate('/admin');
				if (isJobseekerLoggedIn) {
					// navigate('/dashboard');
					navigate('/my-applications');
				} else if (isMentorLoggedIn) navigate('/mentors/interviews');
			}
		});

		dyteMeeting.meeting.participants.joined.addListener('participantJoined', participant => {
			setParticipant(participant);
			setHostJoined(isHost || true);
		});

		dyteMeeting.meeting.participants.joined.addListener('audioUpdate', participant => {
			setParticipant(participant);
		});

		dyteMeeting.meeting.participants.joined.addListener('participantLeft', () => {
			setParticipant(undefined);
			setHostJoined(isHost || false);
		});

		dyteMeeting.meeting.participants.joined.addListener('screenShareUpdate', participant => {
			if (participant.screenShareEnabled) setScreenShareParticipant(participant);
			else setScreenShareParticipant(undefined);
		});

		return () => {
			dyteMeeting.meeting.participants.joined.removeAllListeners();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (ongoingAssessData?.hasAssessmentEnded) setHasAssessmentEnded(true);
		else if (ongoingAssessData?.ongoingAssess) {
			setOngoingAssessDetails(ongoingAssessData.ongoingAssess);
		} else setOngoingAssessDetails(undefined);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ongoingAssessData]);

	return (
		<>
			<InterviewNavbar joined={joined} />
			<div className="mx-5">
				{joined ? (
					hostJoined || meeting?.isAIInterview ? (
						<PostJoinedScreen participant={participant} screenShareParticipant={screenShareParticipant} />
					) : (
						<WaitingForHost />
					)
				) : (
					<JoinConfirmation />
				)}
			</div>
			{joined ? (
				<InterviewFooter
					isHost={isHost}
					showEndMeetingModal={showEndMeetingModal}
					setShowEndMeetingModal={setShowEndMeetingModal}
				/>
			) : (
				<div />
			)}

			<MeetingEndModal
				isOpen={showEndMeetingModal}
				isSuccessfull={isRescheduleRecommended}
				reason={rescheduleReason}
				setIsSuccessfull={setIsRescheduleRecommended}
				setReason={setRescheduleReason}
				setShowEndMeetingModal={setShowEndMeetingModal}
				handleSubmit={() => {
					endMeetingFn.mutate(meeting?._id ?? '');
					setShowEndMeetingModal(false);
				}}
				handleLeave={() => {
					dyteMeeting.meeting.leave();
					setShowEndMeetingModal(false);
				}}
			/>
			<LiveInterviewOverlayModal isOpen={endMeetingFn.isLoading} />
		</>
	);
};

export default LiveInterviewScreen;
